import { useCallback, useEffect, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';

export type IntroField = 'where' | 'concierge' | 'dates';

export type IntroState = {
  typed: Record<IntroField, string | null>; // text currently being typed into each field
  active: IntroField | null; // field showing the caret
  highlight: boolean; // the CTA moment
};

type Suggestion = {
  decoys: string[]; // destinations typed and erased first
  where: string;
  concierge: string;
  dates: string;
};

type Options = Suggestion & {
  // Commits one field's suggestion as its real value, as soon as it is typed
  onCommit: (field: IntroField) => void;
};

const idle: IntroState = { typed: { where: null, concierge: null, dates: null }, active: null, highlight: false };
const fields: IntroField[] = ['where', 'concierge', 'dates'];

const rand = (min: number, max: number) => min + Math.random() * (max - min);

class SkipField extends Error {}

// A human-paced typewriter that suggests a popular request on load:
// types a couple of destinations, erases them, settles on one, then fills the other
// fields and hands over to the CTA. Each field is committed the moment it is typed and
// stays filled. Touching a field takes only that field over — the typewriter skips it
// and carries on with the rest.
export function useRequestIntro({ decoys, where, concierge, dates, onCommit }: Options) {
  const [state, setState] = useState<IntroState>(idle);
  const owned = useRef<Set<IntroField>>(new Set());
  const reduceMotion = useReducedMotion();
  const commitRef = useRef(onCommit);
  commitRef.current = onCommit;

  // The visitor has taken this field over
  const skip = useCallback((field: IntroField) => {
    if (owned.current.has(field)) return;
    owned.current.add(field);
    setState((s) => ({
      ...s,
      typed: { ...s.typed, [field]: null },
      active: s.active === field ? null : s.active,
    }));
  }, []);

  useEffect(() => {
    if (reduceMotion) {
      fields.forEach((f) => !owned.current.has(f) && commitRef.current(f));
      return;
    }

    let unmounted = false;
    const timers = new Set<number>();
    const wait = (ms: number) =>
      new Promise<void>((resolve) => {
        const id = window.setTimeout(() => {
          timers.delete(id);
          resolve();
        }, ms);
        timers.add(id);
      });

    // Waits, then stops the current field's sequence if it was taken over meanwhile
    const step = async (field: IntroField, ms: number) => {
      await wait(ms);
      if (unmounted) throw new Error('unmounted');
      if (owned.current.has(field)) throw new SkipField();
    };

    const set = (field: IntroField, text: string | null) =>
      setState((s) => ({ ...s, typed: { ...s.typed, [field]: text }, active: text === null ? null : field }));

    const type = async (field: IntroField, word: string) => {
      for (let i = 1; i <= word.length; i++) {
        const ch = word[i - 1];
        // Slight hesitation after spaces and punctuation, like a real hand
        await step(field, ch === ' ' || ch === ',' ? rand(160, 260) : rand(70, 150));
        set(field, word.slice(0, i));
      }
    };

    const erase = async (field: IntroField, word: string) => {
      for (let i = word.length - 1; i >= 0; i--) {
        await step(field, rand(40, 70));
        set(field, word.slice(0, i));
      }
    };

    // Runs one field's sequence; if the visitor takes it over, moves on quietly
    const fill = async (field: IntroField, script: () => Promise<void>) => {
      if (owned.current.has(field)) return;
      try {
        await step(field, 350);
        set(field, '');
        await script();
        await step(field, 250);
        commitRef.current(field);
        set(field, null);
      } catch (e) {
        if (!(e instanceof SkipField)) throw e;
      }
    };

    const run = async () => {
      await wait(800);
      await fill('where', async () => {
        for (const d of decoys) {
          await type('where', d);
          await step('where', rand(650, 850));
          await erase('where', d);
          await step('where', rand(280, 380));
        }
        await type('where', where);
      });
      await fill('concierge', () => type('concierge', concierge));
      await fill('dates', () => type('dates', dates));
      if (unmounted) return;
      // The CTA moment
      await wait(300);
      setState({ ...idle, highlight: true });
      await wait(2600);
      if (!unmounted) setState(idle);
    };

    run().catch(() => undefined);
    return () => {
      unmounted = true;
      timers.forEach((t) => window.clearTimeout(t));
    };
    // Runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { state, skip };
}
